import axiosInstance from "./AxiosInstance";

/**
 * Simplified Student Profile service that utilizes the unified backend endpoint.
 * Performs a single API call to fetch all student data (profile, batches, receipts, attendance).
 * 
 * params optionally include:
 * - studentId (for admin view)
 * - includeStudentsList (to populate admin dropdown)
 * - studentSearch (for admin searching)
 * - studentPage/studentLimit (pagination for dropdown)
 */
export const getUnifiedStudentProfile = async (params = {}) => {
  try {
    // 1. Admin Features: Standardized student directory search
    if (params.includeStudentsList || params.studentSearch) {
       /* `fields=options`: the API sends { id, name, email } and skips the
          course / contact-person / branch / department join. This used to
          download the full, joined record of every student on the page and
          then keep four fields of it here. Nothing read the phone. */
       const listRes = await axiosInstance.get("/students", {
         params: {
           search: params.studentSearch || "",
           limit: params.studentLimit || 10,
           page: params.studentPage || 1,
           fields: "options",
         }
       });
       return {
         statusCode: 200,
         data: {
           studentsList: (listRes.data?.data || []).map(s => ({
             id: s.id,
             name: s.name,
             email: s.email,
           })),
           studentsListPagination: listRes.data?.pagination,
           viewer: null 
         }
       };
    }

    // 2. Admin Feature: Virtual Unified Profile (Single-Call Simulation)
    if (params.studentId) {
      const studentId = params.studentId;
      
      // Fetch billing student first to obtain the email for batch tracking
      const studentRes = await axiosInstance.get(`/students/${studentId}`).catch(() => ({ data: { data: null } }));
      const student = studentRes.data?.data;
      if (!student) {
        return { statusCode: 404, isError: true, message: { message: "Student record not found" } };
      }

      // Parallel data aggregation to synthesize a 'Unified' response shape
      const [academicRes, batchesRes, receiptsRes, attendanceSummaryRes] = await Promise.all([
        axiosInstance.get(`/studentprofiles`, { params: { userId: studentId } }).catch(() => ({ data: { data: [] } })),
        student.email ? axiosInstance.get(`/studentbatches`, { params: { email: student.email } }).catch(() => ({ data: { data: [] } })) : Promise.resolve({ data: { data: [] } }),
        axiosInstance.get(`/receipts`, { params: { studentId } }).catch(() => ({ data: { data: [] } })),
        axiosInstance.get(`/attendances/summary`, { params: { studentId } }).catch(() => ({ data: { data: {} } }))
      ]);

      const academicData = Array.isArray(academicRes.data?.data) ? academicRes.data.data[0] : academicRes.data?.data;
      const mappedBatches = (batchesRes.data?.data || []).map(sb => sb.batchId).filter(Boolean);

      // Return a synthesized object that matches the backend's verified student view shape
      return {
        statusCode: 200,
        isError: false,
        data: {
          student: student,
          academincInfo: academicData, // Compatibility with UI expected key 'academincInfo'
          batches: mappedBatches,
          receipts: receiptsRes.data?.data || [],
          totalClasses: attendanceSummaryRes.data?.data?.totalLectures || 0,
          totalAttended: attendanceSummaryRes.data?.data?.presentCount || 0,
          attendancePercentage: attendanceSummaryRes.data?.data?.attendancePercentage || 0,
          batchWiseAttendance: [],
          viewer: null
        }
      };
    }

    // 3. Student View: Native unified backend endpoint
    // Support email param for admin selection
    const response = await axiosInstance.get("/studentprofiles/profile", { params });
    return response.data;
  } catch (error) {
    console.error("Error in getUnifiedStudentProfile:", error);
    if (error.response && error.response.data) {
      return error.response.data;
    }
    return {
      statusCode: 500,
      isError: true,
      message: { message: "Failed to connect to student profile service" },
      errorStack: error
    };
  }
};

/**
 * Backward compatibility: Fetch a student profile directly by ID
 */
export const getStudentProfileByUserId = async (studentId) => {
  return await getUnifiedStudentProfile({ studentId });
};

/**
 * Fetch list of academic profiles (non-unified)
 */
export const getStudentProfileList = async (params = {}) => {
  try {
    const response = await axiosInstance.get("/studentprofiles", { params });
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      return error.response.data;
    }
    throw error;
  }
};
